import { BanFieldsFragment } from '@/generated/graphql';
import { CopyUserId } from '@/components/bans/CopyUserId';
import { UnbanPopover } from '@/components/bans/UnbanPopover';
import { ShieldIcon } from '@/components/ShieldIcon';
import { formatHostDateTime } from '@/utils/formatUserId';
import { toFullS3Url } from '@/utils/toFullS3Url';
import { displaySubmitterName } from '@/utils/displaySubmitterName';
import {
  isUnnamedSubmitter,
  namedSubmitterInitials,
} from '@/components/bans/bannedPersonAvatar';
import { GuestAvatar } from '@/components/bans/GuestAvatar';

function hiddenLabel(count: number) {
  if (count > 0) {
    return `${count} submission${count === 1 ? '' : 's'} hidden`;
  }
  return 'Submissions kept visible';
}

export function BannedPeopleList({
  bans,
  onUnban,
  defaultUnbanOpenId,
}: {
  bans: BanFieldsFragment[];
  onUnban: (ban: BanFieldsFragment) => Promise<void>;
  defaultUnbanOpenId?: string;
}) {
  if (bans.length === 0) {
    return (
      <div
        data-name="BANNED_EMPTY"
        className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed px-6 py-16 text-center"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <ShieldIcon className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold">No one is banned</h3>
        <p className="max-w-md text-sm text-muted-foreground">
          If someone posts something they shouldn&apos;t, choose{' '}
          <span className="font-semibold text-red-400">Ban person</span> on
          their submission in any screen&apos;s Control tab. They&apos;ll show
          up here for all your screens, and you can unban them anytime.
        </p>
      </div>
    );
  }

  return (
    <div data-name="BANNED_LIST" className="divide-y divide-border">
      {bans.map((ban) => {
        const submitter = displaySubmitterName(ban.user);
        return (
          <div
            key={ban.id}
            className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between"
          >
            <div className="flex min-w-0 flex-1 items-start gap-3">
              {ban.sourcePhotoUrl ? (
                <div className="h-10 min-h-10 w-10 min-w-10 shrink-0 overflow-hidden rounded-md">
                  <img
                    src={toFullS3Url(ban.sourcePhotoUrl)}
                    alt=""
                    className="h-10 min-h-10 w-10 min-w-10 shrink-0 rounded-md object-cover blur-[2px]"
                  />
                </div>
              ) : isUnnamedSubmitter(ban.user) ? (
                <GuestAvatar size="md" data-name="BANNED_GUEST_AVATAR" />
              ) : (
                <div
                  data-name="BANNED_INITIALS_AVATAR"
                  className="flex h-10 min-h-10 w-10 min-w-10 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground"
                >
                  {namedSubmitterInitials(submitter.text)}
                </div>
              )}
              <div className="min-w-0 space-y-1">
                <div
                  data-name="BANNED_SUBMITTER_NAME"
                  className="truncate font-medium"
                  title={submitter.title ?? submitter.text}
                >
                  {submitter.text}
                </div>
                <CopyUserId id={ban.user.id} />
                <div className="text-sm text-muted-foreground">
                  Banned on {ban.sourceEvent?.name || 'a screen'}
                  {ban.sourceEngagement?.title
                    ? ` from ${ban.sourceEngagement.title}`
                    : ''}
                </div>
                <div className="text-sm text-muted-foreground">
                  {formatHostDateTime(ban.createdAt)}
                  {' · '}
                  {ban.hiddenCount > 0
                    ? hiddenLabel(ban.hiddenCount)
                    : ban.sourceSubmissionId
                      ? 'Submissions kept visible'
                      : 'No submissions'}
                </div>
                {ban.reason && (
                  <div className="italic text-muted-foreground">
                    &ldquo;{ban.reason}&rdquo;
                  </div>
                )}
              </div>
            </div>
            <div className="shrink-0 self-end md:self-center">
              <UnbanPopover
                name={submitter.text}
                hiddenCount={ban.hiddenCount}
                defaultOpen={defaultUnbanOpenId === ban.id}
                onConfirm={() => onUnban(ban)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
